import { Injectable } from '@angular/core';

/**
 * Type d'entité éditée actuellement dans le configurateur.
 * Sert à enrichir le contexte de navigation capturé dans l'historique.
 */
export type NavigationEntityType =
  | 'game'
  | 'code'
  | 'quiz'
  | 'question'
  | 'structure'
  | 'language';

/**
 * Snapshot du contexte d'édition courant dans le configurateur.
 *
 * - `stepKey` : clé de la step active (ex. `content-multigames`).
 * - `language` : langue active.
 * - `selectedItemId` : id de l'item actuellement édité dans la page d'édition,
 *   ou `null` si l'utilisateur est sur la vue d'ensemble du step.
 * - `entityType` : type d'entité associée à l'item sélectionné (informatif).
 */
export interface NavigationContext {
  stepKey: string | null;
  language: string | null;
  selectedItemId: string | null;
  entityType: NavigationEntityType | null;
}

/**
 * Service singleton qui suit en temps réel la « zone éditée » dans le
 * configurateur (step actif + item sélectionné).
 *
 * Utilisé en mode pull par {@link HistoryManagerService} : au moment de
 * `addToHistory`, le service de l'historique lit l'état courant pour le
 * stocker dans le `HistoryEntry`. Un undo/redo peut ensuite re-naviguer
 * vers la même page d'édition.
 */
@Injectable({ providedIn: 'root' })
export class EditorNavigationContextService {
  private context: NavigationContext = {
    stepKey: null,
    language: null,
    selectedItemId: null,
    entityType: null,
  };

  /** Met à jour la step active et reset l'item sélectionné. */
  setStep(stepKey: string | null, language: string | null): void {
    this.context = {
      stepKey,
      language,
      selectedItemId: null,
      entityType: null,
    };
  }

  /** Marque un item comme actuellement édité. */
  setSelectedItem(itemId: string | null, entityType: NavigationEntityType | null): void {
    this.context = {
      ...this.context,
      selectedItemId: itemId,
      entityType,
    };
  }

  /** L'utilisateur est revenu à la vue d'ensemble du step courant. */
  clearSelectedItem(): void {
    this.context = {
      ...this.context,
      selectedItemId: null,
      entityType: null,
    };
  }

  /**
   * Retourne une copie immuable du contexte courant.
   * Retourne `null` si aucune step n'a encore été enregistrée.
   */
  getSnapshot(): NavigationContext | null {
    if (!this.context.stepKey) {
      return null;
    }
    return { ...this.context };
  }
}
